/* eslint-disable @typescript-eslint/no-explicit-any */
import * as THREE from 'three';
import TWEEN from './Tween';
import { OrbitControls } from './OrbitControls';
import { setLiveCanvas3D, clearLiveCanvas3D } from './canvas3dRegistry';
import { stackedAhuHeight } from '../components/hybridZStack';

/**
 * Phase 6b — Canvas3D BOOTSTRAP slice, a faithful port of
 * django/ahu/static/ahu/mvc/view/canvas/canvas.view3d.js (+ the camera/scale
 * contracts its wrapper view/canvas/tunnel.js owns: getAhuSizes,
 * updateCameraProps, redrawCanvas). Scope per the 6b split
 * (PHASE6_SPIKE_THREEJS.md §5): empty scene → camera-preset parity. NO section
 * geometry — every draw entry point is a 6c attach marker.
 *
 * Ported bootstrap surfaces (legacy line refs in method docs):
 *  - two scenes + two OrthographicCameras (frustum ±el.offsetWidth/25,
 *    ±el.offsetHeight/25, near 1 far 1000), scene pre-rotation 0.4 (parity 37/38)
 *  - single WebGLRenderer over a manually-acquired context
 *    (webgl2→webgl→experimental-webgl), DPR clamp [1.5, 2] (parity 34-36)
 *  - GridHelper(280, 280, 0xf1f1f1) ×2 at scale 1.5, fixed grid hidden (parity 40)
 *  - dirty-flag rAF loop, two passes per frame, autoClear (parity 39/41)
 *  - custom OrbitControls with {updatePosition, changeZoom} hooks + disabled
 *    grid-camera controls, damping 0.5 (parity 42), grid-swap rules (parity 43)
 *  - the 6 deterministic camera presets + TWEEN preset animation (parity 58-61)
 *  - auto-360 (speed formula, dim hiding, default-view snap — parity 44)
 *  - zoom steps ±0.05 in (0.3 … ~2.05) (parity 64)
 *  - world-scale contract getAhuSizes (coef clamp [0.1,0.25], scene.scale cap
 *    1.15, next_to detection — parity 50/51)
 *  - resize / redraw:canvas contracts (parity 48) + full disposal (parity 49)
 *
 * [NEG]s honored (inventory Part 1 §4): #1 createRenderer throws instead of
 * recursing; #5 zoom() is a plain method; #8 useLegacyLights/checkShaderErrors
 * dropped; #11 is_position_changed is an instance field; #12 camera.aspect +
 * cameraPosition dropped; #15 commented relics not built.
 *
 * DOM re-homes (§3.5 → React state via hooks): `#canvas-current-view .active`
 * and `.views_list_li.active` reads/writes → `activePreset` + onViewChange;
 * `#tunnels-indexes` jQuery render → onActiveTunnelIndexChange /
 * onTunnelsIndexesVisible (pills are a React component). NOTE the vendored
 * OrbitControls still strips both `.active` classes from the live DOM during
 * orbit (wrapper-only copy) — onViewChange(null) keeps React state consistent
 * with that, so a re-render does not resurrect the classes.
 */

export type CanvasViewPreset =
  | 'default'
  | 'plan'
  | 'elevation_near_side'
  | 'front_left'
  | 'back_left'
  | 'back_right';

export const views: Record<CanvasViewPreset, readonly [number, number, number]> = {
  default: [51.26, 23.79, 21],
  plan: [0, 23.79, 0],
  elevation_near_side: [0, 0, 23.79],
  front_left: [21, 23.79, -51.26],
  back_left: [-21, 23.79, 51.26],
  back_right: [-51.26, 23.79, -21],
};

/** GridHelper colour, both grids (legacy canvas.view3d:192-209). */
const GRID_COLOR = 0xf1f1f1;
/**
 * Grid extent, in grid-local units (reported 2026-10-16, "the grid fills only
 * part of the available space"). Both helpers are finite planes, so they cover
 * the canvas only as far as their own edge: past that the board is plain white,
 * which reads as the backdrop stopping short in the corners.
 *
 * How far they have to reach is not a constant — the main grid lives in
 * `scene`, whose scale `getAhuSizes` drives from the unit's size (0.107 on a
 * 36-tunnel hybrid, i.e. the plane shrinks to a tenth with the AHU), and the
 * frustum is the element's own size over the camera zoom. Measured on this
 * box at 1248x981: the main grid needs a half-extent of 736 local units on
 * that hybrid against the 140 it has, and even the unscaled fixed grid needs
 * 147 — so the legacy 280 was already a hair short at this viewport before any
 * scaling. `fitGrids()` therefore re-derives the needed half-extent from the
 * live matrices every drawn frame and rebuilds the geometry when it no longer
 * fits, keeping 1 local unit per division so the hatch's on-screen step and
 * colour never move — only where it ends.
 *
 * 280 is the legacy size and stays the floor, so every unit that already
 * covered its canvas (a standard AHU sits at scene scale 1-1.15) draws exactly
 * the geometry it drew before. The cap bounds the worst case: 4000 divisions
 * is ~16k vertices in one LineSegments, and a grid that needs more than that
 * is drawing sub-pixel cells anyway.
 */
const GRID_MIN_DIVISIONS = 280;
const GRID_MAX_DIVISIONS = 4000;
/**
 * Division counts are rounded up to a multiple of this — coarse enough that an
 * orbit does not re-allocate the geometry every frame, and even, so the lines
 * sit on the same local coordinates (the integers) at every count and the
 * hatch never shifts under the unit when the grid grows.
 */
const GRID_DIVISION_STEP = 40;
/** A grow jumps at least this much ahead of the current size (see fitGrid). */
const GRID_GROWTH_FACTOR = 1.3;

/** Scratch vectors for fitGrid() — it runs per drawn frame, so no garbage. */
const gridOrigin = new THREE.Vector3();
const gridAxisX = new THREE.Vector3();
const gridAxisZ = new THREE.Vector3();

/**
 * Plain-data section row the canvas reads (6c — section.model → REST JSON
 * re-home, inventory §21.3). Numeric fields are inches (RAW — the canvas
 * multiplies by coef).
 */
export interface CanvasSection {
  id: number;
  /** Section length (X extent on canvas). */
  length: number;
  /** Server `name` field ("Fan", "Plate Heatex", …) — display-name fallback. */
  name: string | null;
  tag: string | null;
  /** Component type ('fan' | 'coil' | 'energy_wheel' | 'plenum' | …). */
  type: string | null;
  /**
   * Legacy `section.name_on_canvas` is a TRANSIENT in-memory stamp written by
   * the api.js create flows (api.js:70-72/:255) — it does NOT survive a page
   * reload, so on boot it is null and the render naming falls back to `name`
   * (tunnel.js:514). The create-flow writers land in 6e.
   */
  name_on_canvas: string | null;
  /** Ordering comparator (§21.3); render order derives from this. */
  order_rate: number;
  /** Dual-section pairing (Plate/Rotary host). */
  sitting_on_id: number | null;
  /** Interior dims for the casing cube (G3 — Math.round()'d at draw). */
  aw_width: number;
  aw_height: number;
  /** `casing_insulation_thick` — isSectionHasCasing (canvas.view3d:1355). */
  casing_insulation_thick: number;
  /** Truthy → green section-name label (getSectionNameColor :5739). */
  required_length: number | null;
  /**
   * Re-home of the getSectionNameColor UDC probe (:5739 —
   * stateStorage `component.user_defined_component.model`.udc_name_id).
   * Component models are not fetched in the 6c1 bootstrap → undefined = false.
   */
  udc_has_name?: boolean;
  /**
   * Gas-heater selection probe — AHUGasHeater.model truthiness (burner assy
   * name for direct fire, master_gas_heater.model for the indirect types).
   * Same in-flight semantics as udc_has_name: undefined = red.
   */
  gh_has_model?: boolean;
  /**
   * Arrow data re-homes (G33 — legacy reads stateStorage component models,
   * canvas.view3d:5384/:5434/:5449). Wired from the §20.5 #43/#47 bootstrap
   * fetches (6c1 fix round 1); undefined while those are in flight — the
   * legacy pre-fetch defaults then apply (no arrow for a fan section without
   * a fan model; neutral arrow colors). wheel_configuration_id stays on the
   * legacy boot fallback `next_to ? 2 : 1` (the config probe reads a
   * selector-runtime stamp absent at legacy boot too — canvas.view3d:5437).
   */
  fan_orientation_id?: number | null;
  coil_duty_id?: number | null;
  wheel_configuration_id?: number | null;
  /**
   * G29 input — §20.4 #38 dmount rows (re-home of the per-section DMount
   * collections the legacy tab layer feeds into tunnel.js section_dmount).
   * null/undefined while the bootstrap fetch is in flight = legacy pre-tab
   * state (no dmount dims yet).
   */
  dmounts?: Array<{ id: number; dist_from: number; plane_id: number | null }> | null;
}

/** Plain-data tunnel row the canvas reads (Backbone model → REST JSON re-home). */
export interface CanvasTunnel {
  id: number;
  width: number;
  height: number;
  length: number;
  airflow_direction_id: number | null;
  /** Display index for the pills, e.g. "1:1" (§2.3). */
  index: string;
  /** `tunnel.get('base')?.height || 0` (tunnel.js getAhuSizes). */
  baseHeight: number;
  /**
   * Derived in legacy model/tunnel.js from the CMS tunnel index `value`
   * ('2_1' → [2, 1]); `[1] < [0]` means side-by-side (next_to) — risk R5.
   */
  current_index_values: [number, number];
  /**
   * `tunnel.get('base')` object (G6) — `height == null` removes the base
   * (canvas.view3d:3277), `type ∈ 'tube'|'c_channel'` picks the part set.
   */
  base: { height: number | null; type: string | null } | null;
  /** Under-insulation thickness (G4 tunnel casing + tunnelStartYByFloorConstr). */
  under_insulation_thick: number;
  /**
   * G7-G10 gates: tunnel `lug` flag (drawTunnelBase replay, canvas.view3d
   * :3322) and `base_id` (redrawTunnelLugs guard :1540; lug pane fetch gate).
   */
  lug?: boolean | null;
  base_id?: number | null;
  /**
   * isCube plane-1 probe re-home (canvas.view3d:1480 —
   * `tunnel.floor.get('under_insulation_label')`): the tunnel's Floor row
   * field, fetched at bootstrap (GET ahu/floors/<floor_id>); undefined while
   * in flight / no floor = legacy pre-fetch state (no floor-depth opening).
   */
  floor_under_insulation_label?: unknown;
  /** Sections in collection order (order_rate asc — §21.3 comparator). */
  sections: CanvasSection[];

  /**
   * Hybrid AHU tunnel configuration (KA-x). `is_hybrid` is the AHU-level
   * flag (not derived from row/level/position — every tunnel, hybrid AHU or
   * not, carries real row/level/position values off the Phase 0 backfill,
   * so gating on those alone would misfire on a plain multi-tunnel standard
   * AHU). All hybrid position/face-flag logic below checks `is_hybrid` first
   * and is a no-op otherwise, exactly like the legacy canvas.
   */
  is_hybrid?: boolean;
  row?: number;
  level?: number;
  position?: number;
  has_upstream_end_wall?: boolean;
  has_downstream_end_wall?: boolean;
  has_left_side_wall?: boolean;
  has_right_side_wall?: boolean;
  has_roof?: boolean;
  /**
   * The -Y face of the same Walls/Roof & Floor control. Stored per section
   * (AHUSection.has_floor); this is the tunnel payload's `all(s.has_floor)`
   * aggregate — see hiddenFacesForTunnel.
   */
  has_floor?: boolean;
  /**
   * The Data Bar's "Bases" multi-select — the user's manual override of each
   * of the tunnel's four base perimeter members. `null`/`undefined` means "no
   * override, let the outline rule decide", which is what every tunnel from
   * before the feature carries and what keeps the drawing unchanged there.
   *
   * SAME mixed axis convention as the six wall fields above: `upstream` /
   * `downstream` are the physical X− / X+ ends whichever way the airflow runs,
   * `left` / `right` are airflow-relative. `baseSideOverrideFaces` resolves
   * them with the very same two helpers the wall flags use, and
   * `django/ahu/base_sides.py` is where the convention is written down.
   */
  base_side_upstream?: boolean | null;
  base_side_downstream?: boolean | null;
  base_side_left?: boolean | null;
  base_side_right?: boolean | null;
  /**
   * Does this tunnel stand on a base at all? False only once the user has
   * removed every side, or it has no base record at all
   * (`AHUTunnels.builds_base` on the server).
   *
   * Deliberately NOT "all four `base_sides` are false": a tunnel boxed in by
   * its neighbours has all four faces interior to a larger base and every
   * `base_sides` entry false, yet it still sits at base height on that frame.
   * It gates the base parts, the cabinet's lift, the base-thickness dimension
   * and the grid's extent — but NOT `base.height`, which has to stay real so
   * the tunnel keeps its place in `baseFootprintsSharingOutline`'s rectangle
   * merge, exactly as its footprint does in the server's.
   */
  builds_base?: boolean;
  /** Signed absolute upstream-edge X coordinate (in, own reference frame) — see getTunnelPhysicalSpan. */
  x_offset?: number;
  tunnel_offset_direction?: string;
  tunnel_offset?: number;
}

/** React-side receivers for the legacy jQuery/DOM couplings (§3.5). */
export interface Canvas3DHooks {
  /** Breadcrumb + views-list `.active` swap; null = free orbit (no preset). */
  onViewChange(view: CanvasViewPreset | null): void;
  /** #tunnels-indexes active pill (renderTunnelsIndexes active_id). */
  onActiveTunnelIndexChange(id: number | 'all'): void;
  /** #tunnels-indexes visibility (legacy `tunnelsIndexesEl.html('')` / render). */
  onTunnelsIndexesVisible(visible: boolean): void;
}

export interface Canvas3DOptions {
  /** #canvas_section — the sizing element (offsetWidth/Height contract). */
  el: HTMLElement;
  /** canvas#ahu_canvas — the renderer target. */
  canvas: HTMLCanvasElement;
  /** roboto_thin_regular.json Font — 6c labels (loaded at boot, parity 8). */
  font: THREE.Font;
  /** localStorage `canvas_color_mode` boot value ([NEG]#9 read is caller's). */
  showColor: boolean;
  /** Live tunnel rows (ref-backed — resize/getAhuSizes always read fresh). */
  getTunnels(): CanvasTunnel[];
  hooks: Canvas3DHooks;
  /** Accessory-preview mini-canvas mode (Part 3/4) — never set in 6b. */
  previewAccessoryId?: number | null;
}

/** [NEG]#1 replacement: explicit failure instead of unbounded recursion. */
export class CanvasContextError extends Error {
  constructor() {
    super('AHU canvas: failed to acquire a WebGL context (webgl2/webgl/experimental-webgl)');
    this.name = 'CanvasContextError';
  }
}

export class Canvas3D {
  // --- wiring ---
  el: HTMLElement;
  font: THREE.Font;
  hooks: Canvas3DHooks;
  getTunnels: () => CanvasTunnel[];
  previewAccessoryId: number | null;

  // --- §3.2.3 state (reset points matter for parity; filled by 6c) ---
  showColor: boolean;
  need_update = true;
  coef = 0.2;
  animate = false;
  rotationAngle = 0.4;
  sectionCanvasObjects: any[] = [];
  sectionCanvasObjectsObj: Record<string, any> = {};
  accessoryCanvasObjects: any[] = [];
  accessoryCanvasObjectsObj: Record<string, any> = {};
  tunnelsLengths: Record<string, any> = {};
  tunnelsDimensions: Record<string, any> = {};
  tunnelsHeightDimensions: Record<string, any> = {};
  tunnelsLengthDimensions: Record<string, any> = {};
  /**
   * tunnel id -> the id under which its run's total length dimension is filed
   * in tunnelsLengthDimensions. A back-to-back run draws one total for the
   * whole chain, anchored on a single position, so the other positions need a
   * way back to it (drawDmount re-bands that total when dmounts appear).
   * Identity mapping for a one-tunnel chain.
   */
  chainTotalAnchorByTunnelId: Record<string, any> = {};
  tunnelsBaseDimensions: Record<string, any> = {};
  tunnelsSectionsLengthDimensions: Record<string, any> = {};
  tunnelsDimountsDimensions: Record<string, any> = {};
  sectionsHorizontalDimounts: Record<string, any> = {};
  tunnelsByIndexes: Record<string, any> = {};
  tunnelsWireframes: Record<string, any> = {};
  tunnelBases: Record<string, any> = {};
  /**
   * Tunnel ids (as strings) whose base rails are currently tinted by
   * `setHighlightedBases` — the Data Bar base-change highlight. It has to
   * survive a redraw: `drawTunnelBase` disposes and rebuilds the part group
   * it tinted, so it re-applies the tint from this set on the way out.
   */
  highlightedBaseTunnelIds: Set<string> = new Set();
  /**
   * Memo for `combineBaseFootprints`: which bases group into one outline,
   * keyed by the footprints that went in. Every tunnel of a group asks the
   * same question during a redraw, and the answer is a search — see
   * `baseOutlineFacesForTunnel`. Keyed on the geometry, so any edit misses.
   */
  baseGroupingCache: Map<string, any[]> = new Map();
  tunnelsStartX: Record<string, any> = {};
  tunnelsDimensionsInfo: Record<string, any> = {};
  sections_cassing: Record<string, any> = {};
  tunnelStartYByFloorConstr: Record<string, any> = {};
  int_wall: { vertical: Record<string, any>; horizontal: Record<string, any> } = {
    vertical: {},
    horizontal: {},
  };
  coilConnections: Record<string, any> = {};
  evapPackConnections: Record<string, any> = {};
  accessoriesWithError: Record<string, any> = {};
  sectionsWithError: Record<string, any> = {};
  tunnelsAirflowIcons: Record<string, any> = {};
  tunnels_lugs: Record<string, any> = {};
  tunnels_lugs_on_canvas: Record<string, any> = {};
  activeLugs: any[] = [];
  max_selected_lug_count = 2;

  /**
   * Hybrid AHU (KA-x-ruler) — AHU-length ruler scene groups (canvas.view3d
   * `rulerGroupPlan`/`rulerGroupElevation`). Declared on the base class
   * (not Canvas3DGeometry, which builds them) because setView below toggles
   * their visibility on plan/elevation switches without a full rebuild.
   */
  rulerGroupPlan: THREE.Group | null = null;
  rulerGroupElevation: THREE.Group | null = null;

  // --- view/selection bookkeeping ---
  view: CanvasViewPreset | '' = '';
  /**
   * Re-home of the `.views_list_li.active[data-value=…]` DOM reads (§3.5):
   * the currently-active preset entry, null while free-orbiting (the vendored
   * OrbitControls clears the DOM classes during onPointerMove; updatePosition
   * mirrors that here).
   */
  activePreset: CanvasViewPreset | null = 'default';
  activeTunnelIndex: number | null = null;
  /**
   * The tunnel selection setView forced aside — either to "all" (plan) or to
   * "1:1" (elevation/next_to) — `undefined` when no view-driven override is in
   * effect, otherwise the pill that was active before it (null = "all").
   * Restored on the way out to a view that does not force a pill, so leaving
   * Plan does not leave the board on a selection the user never made.
   * Cleared whenever the user picks a pill themselves (changeIndex), so a
   * manual choice made inside Plan wins over the restore.
   */
  tunnelIndexBeforeViewIsolation: number | null | undefined = undefined;
  hideTunnels = false;
  tunnels_length = 0;
  /** [NEG]#11 — instance field replacing the accidental window-global channel. */
  is_position_changed = false;
  text_position: 'front' | 'top' = 'top';
  text_position_changed: 'front' | 'top' | '' = '';
  text_view_position: string = '';
  last_view: string = '';

  // --- getAhuSizes stamps (tunnel.js §3.1; world-scale contract) ---
  ahu_height = 0;
  ahu_width = 0;
  ahu_length = 0;
  tunels_count = 0;
  max_length = 0;
  max_height = 0;
  max_width = 0;
  next_to = false;
  max_base_height = 0;
  longestTunnelDirection: number | null = 1;
  max_tunnel_airflow: number | null = null;
  firstTunnel: number | null = null;
  lastTunnel: number | null = null;
  firstTunnelBaseHeight = 0;
  firstTunnelWidth = 0;
  firstTunnelLength = 0;
  firstTunnelHeight = 0;

  // --- three graph ---
  scene: THREE.Scene;
  group: THREE.Group;
  sceneForGrid: THREE.Scene;
  tunnelsWireframeGroup: THREE.Group;
  camera: THREE.OrthographicCamera;
  cameraForGrid: THREE.OrthographicCamera;
  renderer!: THREE.WebGLRenderer;
  gridHelper: THREE.GridHelper;
  fixedGridHelper: THREE.GridHelper;
  /** Divisions each grid's geometry currently carries (see GRID_MIN_DIVISIONS). */
  private gridDivisions = GRID_MIN_DIVISIONS;
  private fixedGridDivisions = GRID_MIN_DIVISIONS;
  raycaster: THREE.Raycaster;
  pointer: THREE.Vector2;
  _controls: OrbitControls | null = null;
  _controlsForGrid: OrbitControls | null = null;

  private rafId: number | null = null;
  private disposed = false;

  constructor({
    el,
    canvas,
    font,
    showColor,
    getTunnels,
    hooks,
    previewAccessoryId = null,
  }: Canvas3DOptions) {
    this.el = el;
    this.font = font;
    this.hooks = hooks;
    this.getTunnels = getTunnels;
    this.previewAccessoryId = previewAccessoryId;
    // change_canvas_color_mode subscription lives in <AhuCanvas> (bus); the
    // preview canvas forces colorless (§3.2.3).
    this.showColor = previewAccessoryId != null ? false : showColor;

    // Scene graph (canvas.view3d:167-175): scene ← group + wireframe group +
    // gridHelper; sceneForGrid ← fixedGridHelper; both pre-rotated 0.4.
    this.scene = new THREE.Scene();
    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.sceneForGrid = new THREE.Scene();
    this.tunnelsWireframeGroup = new THREE.Group();
    this.scene.add(this.tunnelsWireframeGroup);
    this.scene.rotation.y = this.rotationAngle;
    this.sceneForGrid.rotation.y = this.rotationAngle;

    // Cameras (:177-185). Frustum = ±offsetWidth/25, ±offsetHeight/25, near 1,
    // far 1000; both start at views.default looking at origin. Legacy order
    // kept (grid camera lookAt before position.set — its controls' first
    // update() re-derives the orientation). [NEG]#12: camera.aspect = 1 and
    // the vestigial cameraPosition are dropped.
    this.camera = new THREE.OrthographicCamera(
      this.el.offsetWidth / -25,
      this.el.offsetWidth / 25,
      this.el.offsetHeight / 25,
      this.el.offsetHeight / -25,
      1,
      1000
    );
    this.cameraForGrid = new THREE.OrthographicCamera(
      this.el.offsetWidth / -25,
      this.el.offsetWidth / 25,
      this.el.offsetHeight / 25,
      this.el.offsetHeight / -25,
      1,
      1000
    );
    this.camera.position.set(...views.default);
    this.camera.updateMatrix();
    this.camera.lookAt(this.scene.position);
    this.cameraForGrid.lookAt(this.scene.position);
    this.cameraForGrid.position.set(...views.default);
    this.cameraForGrid.updateMatrix();

    this.createRenderer(canvas);
    this.renderer.shadowMap.enabled = false;
    // DPR clamped to [1.5, 2] (:188, parity 36).
    this.renderer.setPixelRatio(Math.min(Math.max(1.5, window.devicePixelRatio), 2));
    this.renderer.setSize(this.el.offsetWidth, this.el.offsetHeight);
    // [NEG]#8: renderer.useLegacyLights does not exist in r110 — dropped.

    // Grids (:192-209): GridHelper(280, 280, 0xf1f1f1) ×2, scale 1.5, fixed
    // grid initially hidden. Grid Y moves with calculateCubePositions (6c).
    // 280 is the legacy size and stays the floor; fitGrids() grows it from
    // here whenever the plane no longer reaches the corners of the frustum.
    const gridSize = GRID_MIN_DIVISIONS;
    const gridStep = GRID_MIN_DIVISIONS;
    const gridColor = GRID_COLOR;
    this.gridHelper = new THREE.GridHelper(gridSize, gridStep, gridColor, gridColor);
    this.fixedGridHelper = new THREE.GridHelper(gridSize, gridStep, gridColor, gridColor);
    this.fixedGridHelper.visible = false;
    this.gridHelper.scale.set(1.5, 1.5, 1.5);
    this.fixedGridHelper.scale.set(1.5, 1.5, 1.5);
    this.scene.add(this.gridHelper);
    this.sceneForGrid.add(this.fixedGridHelper);

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();

    // Register this as the board's live Canvas3D (module-level singleton — was
    // window.__c3d). The accessory selector's mini-3D preview clones the live
    // canvas caches off this registry (useAccessoryPreviewCanvas). Cleared in
    // dispose() so a route change leaves no stale handle.
    //
    // Guarded to real board canvases only (previewAccessoryId == null): the
    // preview canvas construction (createAccessoryPreviewCanvas) runs THIS
    // SAME constructor chain, so an unguarded call here re-registered the
    // registry to point at the PREVIEW instead of the board — invisibly, on
    // every "AHU" mini-3D tab open. Closing that dialog then disposed the
    // preview, whose dispose() clears the registry (see below) because it was
    // — wrongly — the one holding the handle, leaving it null. The board
    // canvas itself never re-registers (its own constructor already ran once
    // at mount), so every later preview attach found no live canvas to clone
    // and rendered nothing: the mini-3D tab going permanently blank after the
    // first "select AHU tab, close, reopen" cycle.
    if (this.previewAccessoryId == null) setLiveCanvas3D(this);

    // Picking/keyboard listeners (:215-326) are sub-phase 6d; the lifecycle
    // listeners (contextlost/focus/beforeunload/resize/#forms-main-container)
    // are owned by <AhuCanvas> effects with cleanup (R1/R8 — no reset leak).

    this.createViewControls();
  }

  /**
   * createRenderer (canvas.view3d:328-347): manual context acquisition
   * webgl2 → webgl → experimental-webgl with {alpha, antialias}, handed to an
   * external-context WebGLRenderer. [NEG]#1: no context → CanvasContextError
   * (legacy recursed unboundedly). [NEG]#8: checkShaderErrors ctor option
   * dropped (not a real r110 option).
   */
  createRenderer(canvas: HTMLCanvasElement): void {
    const attrs: WebGLContextAttributes = { alpha: true, antialias: true };
    const context = (canvas.getContext('webgl2', attrs) ||
      canvas.getContext('webgl', attrs) ||
      canvas.getContext('experimental-webgl', attrs)) as WebGLRenderingContext | null;

    if (!context) throw new CanvasContextError();

    // failIfMajorPerformanceCaveat is kept for fidelity (legacy ctor option;
    // inert with an externally-acquired context) — r110's d.ts predates it.
    const params: THREE.WebGLRendererParameters & { failIfMajorPerformanceCaveat?: boolean } = {
      canvas,
      context,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: false,
      failIfMajorPerformanceCaveat: false,
    };
    this.renderer = new THREE.WebGLRenderer(params);
  }

  /**
   * createViewControls (canvas.view3d:441-472). First call constructs both
   * controls and starts the render loop (the AMD Promise.all of the legacy is
   * a sync ESM import here — same semantics, immediate). Subsequent calls are
   * the `canvas:rotate` path: autoRotate + grid swap + speed formula
   * min(22, max(7, (1 + max_length·coef/5)·0.8)) + hide all dimension groups
   * while rotating (parity 44).
   */
  createViewControls(autoRotate?: boolean): void {
    if (!this._controls) {
      this._controls = new OrbitControls(this.camera, this.renderer.domElement, {
        updatePosition: this.updatePosition.bind(this),
        changeZoom: this.changeZoom.bind(this),
      });
      this._controlsForGrid = new OrbitControls(this.cameraForGrid, this.renderer.domElement);
      this._controlsForGrid.enabled = false;
      this._controlsForGrid.enableRotate = false;
      this._controls.damping = 0.5;
      this._controls.autoRotate = false;
      this.render();
      return;
    }
    this._controls.autoRotate = !!autoRotate;
    this.gridHelper.visible = !autoRotate;
    this.fixedGridHelper.visible = !!autoRotate;
    const speed = (1 + (this.max_length * this.coef) / 5) * 0.8;
    this._controls.autoRotateSpeed = Math.min(22, Math.max(7, speed));
    const dmounts: any[] = [];
    Object.values(this.tunnelsDimountsDimensions).map((el: any) => el && dmounts.push(...el));
    this.showOrHideDimenssions(
      [
        ...Object.values(this.tunnelsDimensions),
        ...Object.values(this.tunnelsHeightDimensions),
        ...Object.values(this.tunnelsLengthDimensions),
        ...Object.values(this.tunnelsBaseDimensions),
        ...dmounts,
        ...Object.values(this.tunnelsSectionsLengthDimensions),
      ],
      !autoRotate
    );
  }

  /**
   * Render loop (canvas.view3d:473-484): unconditional rAF self-reschedule,
   * dirty-flag gate (need_update || autoRotate), autoClear, grid pass first
   * then main pass, controls.update(). TWEEN.update runs in the preset
   * animation's own rAF wrapper, not here (§3.2.5). React addition: the loop
   * stops when disposed (legacy never unmounts — parity 49).
   */
  render = (): void => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.render);
    if (!this.need_update && !this._controls?.autoRotate) return;
    this.need_update = false;
    this.renderer.autoClear = true;
    this.renderer.render(this.sceneForGrid, this.cameraForGrid);
    this.renderer.render(this.scene, this.camera);
    this.fitGrids();
    this._controls?.update();
    if (this.view === 'plan' || this.view === 'elevation_near_side') {
      this.showOrHideDimenssions([this.tunnelsHeightDimensions[1]], !this.activeTunnelIndex);
    }
  };

  /**
   * Grow whichever grid no longer reaches the corners of its camera's frustum
   * (GRID_MIN_DIVISIONS). Runs at the end of a drawn frame — i.e. only when
   * something moved, and with `matrixWorld` already up to date from the render
   * passes just above, so it costs a handful of vector ops and never forces a
   * second scene traversal. A rebuild re-dirties the loop so the new geometry
   * is on screen on the next frame.
   */
  private fitGrids(): void {
    const main = this.fitGrid(this.gridHelper, this.camera, this.gridDivisions);
    const fixed = this.fitGrid(this.fixedGridHelper, this.cameraForGrid, this.fixedGridDivisions);
    if (main === this.gridDivisions && fixed === this.fixedGridDivisions) return;
    this.gridDivisions = main;
    this.fixedGridDivisions = fixed;
    this.need_update = true;
  }

  /**
   * One grid: the division count it needs to cover `camera`, rebuilt into its
   * geometry if that differs from `current`. Returns the count it now carries.
   *
   * The helper's lines run along its local X and Z, so a point of the plane is
   * `origin + u·axisX + v·axisZ`; taking all three through `matrixWorld` then
   * `matrixWorldInverse` puts them in camera space, where the orthographic
   * frustum is an axis-aligned rectangle and the z component is just depth.
   * Inverting that 2x2 for each of the four corners gives the (u, v) the plane
   * must reach — which is where the scene scale, the camera zoom, the element
   * size, the view preset and the helper's own offset all land, so there is
   * nothing left to special-case. Shrink only at half the current size, so a
   * zoom that drifts across a boundary cannot ping-pong between two geometries.
   */
  private fitGrid(
    helper: THREE.GridHelper,
    camera: THREE.OrthographicCamera,
    current: number
  ): number {
    // A hidden grid is one of the pair that is swapped out (zoom, elevation,
    // auto-rotate): leave it alone and fit it when it comes back.
    if (!helper.visible) return current;
    const origin = gridOrigin
      .set(0, 0, 0)
      .applyMatrix4(helper.matrixWorld)
      .applyMatrix4(camera.matrixWorldInverse);
    const axisX = gridAxisX
      .set(1, 0, 0)
      .applyMatrix4(helper.matrixWorld)
      .applyMatrix4(camera.matrixWorldInverse)
      .sub(origin);
    const axisZ = gridAxisZ
      .set(0, 0, 1)
      .applyMatrix4(helper.matrixWorld)
      .applyMatrix4(camera.matrixWorldInverse)
      .sub(origin);
    const det = axisX.x * axisZ.y - axisZ.x * axisX.y;
    // Edge-on (an elevation view, or mid-orbit through the horizon) — the
    // plane covers no area at all and the needed extent is unbounded. The grid
    // in that state is the fixed one anyway.
    if (!Number.isFinite(det) || Math.abs(det) < 1e-6) return current;
    const halfWidth = (camera.right - camera.left) / (2 * camera.zoom);
    const halfHeight = (camera.top - camera.bottom) / (2 * camera.zoom);
    const centerX = (camera.right + camera.left) / 2;
    const centerY = (camera.top + camera.bottom) / 2;
    let needed = 0;
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        const x = centerX + sx * halfWidth - origin.x;
        const y = centerY + sy * halfHeight - origin.y;
        const u = (x * axisZ.y - y * axisZ.x) / det;
        const v = (axisX.x * y - axisX.y * x) / det;
        needed = Math.max(needed, Math.abs(u), Math.abs(v));
      }
    }
    if (!Number.isFinite(needed)) return current;
    // `needed` is a half-extent; the helper is sized by its full width, and one
    // division is one local unit, so the count is twice that, rounded up. A
    // grow also takes at least GRID_GROWTH_FACTOR of what is there, so a drag
    // that walks the needed size up a thousand units rebuilds a handful of
    // times instead of once per quantum.
    let divisions = Math.ceil((2 * needed) / GRID_DIVISION_STEP) * GRID_DIVISION_STEP;
    if (divisions > current) {
      divisions = Math.max(
        divisions,
        Math.ceil((current * GRID_GROWTH_FACTOR) / GRID_DIVISION_STEP) * GRID_DIVISION_STEP
      );
    }
    divisions = Math.min(GRID_MAX_DIVISIONS, Math.max(GRID_MIN_DIVISIONS, divisions));
    if (divisions === current || (divisions < current && divisions > current / 2)) return current;
    const rebuilt = new THREE.GridHelper(divisions, divisions, GRID_COLOR, GRID_COLOR);
    helper.geometry.dispose();
    helper.geometry = rebuilt.geometry;
    (rebuilt.material as THREE.Material).dispose();
    return divisions;
  }

  /**
   * renderTunnelsIndexes (canvas.view3d:485-503), re-homed: the pill DOM is a
   * React component; this keeps the legacy decisions — bail under 2 tunnels,
   * retarget a stale activeTunnelIndex to tunnel "1:1" on plan(stacked)/
   * elevation(next_to), re-apply tunnel isolation, hideTunnels flag. (The
   * plan leg of the retarget is dead now setView forces plan to "all"; it is
   * kept so any other caller reaching here mid-plan behaves as legacy did.)
   */
  renderTunnelsIndexes(view: CanvasViewPreset | '' = ''): void {
    const val = view || this.activePreset;
    const plan_or_elevation =
      (val === 'plan' && !this.next_to) || (val === 'elevation_near_side' && this.next_to);
    this.hooks.onTunnelsIndexesVisible(false);

    const list = this.getTunnels();
    if (list.length < 2) return;
    if (
      plan_or_elevation &&
      this.activeTunnelIndex &&
      !list.find((el) => el.id === this.activeTunnelIndex)
    ) {
      this.activeTunnelIndex = list.find((el) => el.index === '1:1')?.id ?? null;
    }
    this.hooks.onActiveTunnelIndexChange(this.activeTunnelIndex || 'all');
    this.hooks.onTunnelsIndexesVisible(true);
    if (this.activeTunnelIndex) this.hideOrShowTunnels(false, this.activeTunnelIndex);
    this.hideTunnels = true;
  }

  /**
   * hideOrShowTunnels (canvas.view3d:524-553) — tunnel isolation over the
   * geometry registries (empty until 6c; the bookkeeping must behave now so
   * pill state survives 6c attach).
   */
  hideOrShowTunnels(value: boolean | 'all' = false, tunnel_id?: number | null, prev_tunnel_id?: number | 'all' | null): void {
    const val = !!value;
    this.activeTunnelIndex = tunnel_id ?? null;
    this.hideTunnels = !val;
    Object.entries(this.tunnelsWireframes).forEach((tunnel: [string, any]) => {
      if (tunnel[0] != String(tunnel_id)) tunnel[1].visible = val;
      else if (prev_tunnel_id || tunnel[0] == String(tunnel_id)) tunnel[1].visible = true;
    });
    this.sectionCanvasObjects.forEach((el: any) => {
      if (el.tunnel.id != tunnel_id) {
        el.cube.visible = val;
        el.wireframe.visible = val;
      } else if (prev_tunnel_id || el.tunnel.id == tunnel_id) {
        el.cube.visible = true;
        el.wireframe.visible = true;
      }
    });
    Object.entries(this.tunnelBases).forEach((tunnel: [string, any]) => {
      if (tunnel[0] != String(tunnel_id)) {
        tunnel[1].cube.visible = val;
        tunnel[1].group.visible = val;
        tunnel[1].wireframe.visible = val;
      } else if (prev_tunnel_id || tunnel[0] == String(tunnel_id)) {
        tunnel[1].cube.visible = true;
        tunnel[1].group.visible = true;
        tunnel[1].wireframe.visible = true;
      }
    });
    this.showOrHideDimenssions([this.tunnelsHeightDimensions[1]], !this.activeTunnelIndex);
  }

  /** changeIndex (canvas.view3d:554-560) — `canvas:change:tunnel:index`. */
  changeIndex(tunnel_id: number | 'all', prev_tunnel_id?: number | 'all'): void {
    this.need_update = true;
    // A pill click is the user's own choice — drop the pre-isolation memory so
    // leaving the view does not overwrite it (see tunnelIndexBeforeViewIsolation).
    this.tunnelIndexBeforeViewIsolation = undefined;
    if (tunnel_id === 'all') this.hideOrShowTunnels('all');
    else this.hideOrShowTunnels(false, tunnel_id, prev_tunnel_id);
    this.showHideLugsOnAdjoiningBase();
  }

  /**
   * setViewDirection_with_animation (canvas.view3d:578-729) — faithful port:
   * polar interpolation around the hardcoded (0,30,30) reference, short-arc
   * selection, TWEEN alpha → chained beta (delay 100), duration
   * 2000ms·Δangle/2π each, Circular.InOut, camera positions derived by
   * component-wise scaling of the initial position (NaN-prone but matches
   * legacy — parity 59), own rAF wrapper pumping TWEEN.update + need_update.
   */
  setViewDirection_with_animation(view: CanvasViewPreset): void {
    const self = this;
    self.need_update = true;

    class Animation {
      private isRunning = false;
      constructor(private onAnimate: () => void) {}
      start(): this {
        this.isRunning = true;
        const animate = (): void => {
          if (this.isRunning) {
            requestAnimationFrame(animate);
            this.onAnimate();
          }
        };
        animate();
        return this;
      }
      finish(): this {
        this.isRunning = false;
        return this;
      }
    }

    const targetDirection = new THREE.Vector3(...views[view]).clone().normalize();
    const currentDirection = new THREE.Vector3(0, 30, 30).clone().normalize();
    const currentDirectionInit = this.camera.position.clone();
    const currentDirectionInitNormalized = this.camera.position.clone().normalize();

    function accurateCos(angle: number): number {
      let cosX = Math.cos(angle);
      if (Math.abs(cosX) < 0.00001) cosX = 0;
      return cosX;
    }
    function accurateSin(angle: number): number {
      let sinX = Math.sin(angle);
      if (Math.abs(sinX) < 0.00001) sinX = 0;
      return sinX;
    }
    function calcAngleAlpha(vector: THREE.Vector3): number {
      // get vector's projection on XY plane
      const v = vector.clone().setZ(0);
      if (v.length() === 0) {
        // set angle for vector (0,0, +/- 1)
        return -Math.PI / 2;
      }
      const axisX = new THREE.Vector3(1, 0, 0);
      const cosAlpha = vector.dot(axisX) / (v.length() * axisX.length());
      // Clamp cosinus to handle numerical problems:
      let angle = Math.acos(THREE.Math.clamp(cosAlpha, -1, 1));
      // Map angle from [0,PI] to [-PI, +PI] interval
      if (v.y < 0) angle = -angle;
      return angle;
    }
    // Beta = angle between vector and Z-axis, interval [-PI/2, +PI/2].
    function calcAngleBeta(vector: THREE.Vector3): number {
      return Math.asin(vector.z);
    }

    const currentPolar = {
      alpha: calcAngleAlpha(currentDirection),
      beta: calcAngleBeta(currentDirection),
    };
    const targetPolar = {
      alpha: calcAngleAlpha(targetDirection),
      beta: calcAngleBeta(targetDirection),
    };
    // Select short arc (cw vs ccw) from current to target.
    (function setShortArc(cur: typeof currentPolar, tgt: typeof targetPolar) {
      const arcAngle = tgt.alpha - cur.alpha;
      if (Math.abs(arcAngle) > Math.PI) {
        if (arcAngle > Math.PI) tgt.alpha -= 2 * Math.PI;
        else if (arcAngle < -Math.PI) cur.alpha -= 2 * Math.PI;
      }
    })(currentPolar, targetPolar);

    function calcAnimationTime(fromAngle: number, toAngle: number): number {
      const TIME_2PI = 2000;
      return (TIME_2PI * Math.abs(toAngle - fromAngle)) / (2 * Math.PI);
    }

    const tweenAlpha = new TWEEN.Tween(currentPolar)
      .to({ alpha: targetPolar.alpha }, calcAnimationTime(currentPolar.alpha, targetPolar.alpha))
      .onUpdate(function () {
        currentDirection.x = Math.cos(currentPolar.beta) * accurateCos(currentPolar.alpha);
        currentDirection.y = Math.cos(currentPolar.beta) * accurateSin(currentPolar.alpha);
        const p = {
          x: currentDirectionInit.x / (currentDirectionInitNormalized.x / currentDirection.x),
          y: currentDirectionInit.y / (currentDirectionInitNormalized.y / currentDirection.y),
          z: currentDirectionInit.z / (currentDirectionInitNormalized.z / currentDirection.z),
        };
        self.camera.position.copy(p as unknown as THREE.Vector3);
        self.camera.updateProjectionMatrix();
        self.camera.updateMatrix();
        self.camera.lookAt(self.scene.position);
      })
      .onStart(function () {})
      .easing(TWEEN.Easing.Circular.InOut);

    const tweenBeta = new TWEEN.Tween(currentPolar)
      .to({ beta: targetPolar.beta }, calcAnimationTime(currentPolar.beta, targetPolar.beta))
      .onUpdate(function () {
        currentDirection.z = Math.sin(currentPolar.beta);
        currentDirection.x = Math.cos(currentPolar.beta) * accurateCos(targetPolar.alpha);
        currentDirection.y = Math.cos(currentPolar.beta) * accurateSin(targetPolar.alpha);
        const p = {
          x: currentDirectionInit.x / (currentDirectionInitNormalized.x / currentDirection.x),
          y: currentDirectionInit.y / (currentDirectionInitNormalized.y / currentDirection.y),
          z: currentDirectionInit.z / (currentDirectionInitNormalized.z / currentDirection.z),
        };
        self.camera.position.copy(p as unknown as THREE.Vector3);
        self.camera.updateProjectionMatrix();
        self.camera.updateMatrix();
        self.camera.lookAt(self.scene.position);
      })
      .easing(TWEEN.Easing.Circular.InOut);

    const animation = new Animation(function () {
      TWEEN.update();
      self.need_update = true;
    }).start();

    tweenAlpha
      .chain(
        tweenBeta.delay(100).onComplete(function () {
          animation.finish();
        })
      )
      .start();
  }

  /**
   * setView (canvas.view3d:730-780). DOM re-homes: breadcrumb/views-list
   * `.active` swap → hooks.onViewChange; tunnelsIndexesEl.html('') →
   * onTunnelsIndexesVisible(false). Everything else verbatim: controls.reset()
   * (snaps to the saved default state), changeZoom(), snap-or-animate to the
   * preset, scene rotation 0/0.4, grid choice (fixed for elevation), label
   * visibility per text_position (registries empty until 6c), the tunnel-pill
   * override (plan → "all", elevation/next_to → "1:1"), accessory re-dash
   * (6c), rotateTextIfNessecery.
   */
  setView(view: CanvasViewPreset = 'default', animate = false): void {
    this.need_update = true;
    this.view = view;
    const prevView = this.activePreset; // $('#canvas-current-view .active').data('value')
    this.activePreset = view;
    this.hooks.onViewChange(view);
    this.hooks.onTunnelsIndexesVisible(false);
    this._controls?.reset();
    this.changeZoom();
    if (animate) this.setViewDirection_with_animation(view);
    else this.camera.position.set(...views[view]);
    this.camera.updateMatrix();
    this.camera.updateProjectionMatrix();
    this.scene.rotation.y = view === 'plan' || view === 'elevation_near_side' ? 0 : 0.4;
    const text_position_changed = this.text_position_changed || this.text_position;

    this.gridHelper.visible = view !== 'elevation_near_side';
    this.fixedGridHelper.visible = view === 'elevation_near_side';
    // Hybrid AHU (KA-x-ruler) — the ruler is built once per full render in
    // both orientations (see drawRuler) so a plain view switch — no
    // re-render needed — can just flip which one is visible.
    if (this.rulerGroupPlan) this.rulerGroupPlan.visible = view !== 'elevation_near_side';
    if (this.rulerGroupElevation) this.rulerGroupElevation.visible = view === 'elevation_near_side';
    if (view === 'plan') {
      this.sectionCanvasObjects.forEach((el: any) => {
        el.textLabel.visible = true;
        el.textLabelRotated.visible = false;
      });
      this.text_position_changed = 'top';
    } else if (view === 'elevation_near_side') {
      this.sectionCanvasObjects.forEach((el: any) => {
        el.textLabel.visible = false;
        el.textLabelRotated.visible = true;
      });
      this.text_position_changed = 'front';
    } else if (this.text_position !== text_position_changed && view !== prevView) {
      this.sectionCanvasObjects.forEach((el: any) => {
        el.textLabel.visible = this.text_position === 'top';
        el.textLabelRotated.visible = this.text_position === 'front';
      });
      this.text_position_changed = this.text_position;
    }
    const list = this.getTunnels();
    if (list.length > 1) {
      // Plan always shows the whole AHU: the tunnel isolation a section click
      // leaves behind (Canvas3DGeometry.onSectionAction) must not follow the
      // view in, whichever section happens to be selected.
      // DEVIATION from legacy (canvas.view3d:775), reported behaviour: legacy
      // force-selected "1:1" on a stacked AHU here, on the grounds that plan
      // draws stacked tunnels over each other. That forcing is dropped.
      const forcesAll = view === 'plan';
      // Elevation on a side-by-side AHU still auto-isolates to "1:1" (same
      // overlap argument, untouched by this change).
      const needsIsolation = view === 'elevation_near_side' && this.next_to;
      // DEDRIFT: legacy never undid its forcing — it survived the next view
      // change and the board came back showing one tunnel. Either override
      // remembers the previous pill and puts it back on the way out.
      if (forcesAll || needsIsolation) {
        if (this.tunnelIndexBeforeViewIsolation === undefined) {
          this.tunnelIndexBeforeViewIsolation = this.activeTunnelIndex;
        }
      }
      if (forcesAll) {
        // hideOrShowTunnels rather than changeIndex('all'): the registries have
        // to be re-shown, but changeIndex would clear the pre-view pill we just
        // stored. renderTunnelsIndexes below syncs the pill label.
        this.hideOrShowTunnels('all');
        this.showHideLugsOnAdjoiningBase();
      } else if (needsIsolation) {
        this.activeTunnelIndex = list.find((el) => el.index === '1:1')?.id ?? null;
      } else if (this.tunnelIndexBeforeViewIsolation !== undefined) {
        // Through changeIndex so the restored state is exactly the pill-click
        // state (registries re-shown + showHideLugsOnAdjoiningBase), not just
        // the pill label. It clears tunnelIndexBeforeViewIsolation itself.
        this.changeIndex(this.tunnelIndexBeforeViewIsolation ?? 'all');
      }
      this.renderTunnelsIndexes(view);
    }
    // Accessory wireframe re-dash (canvas.view3d:775 — makeLineDashed over
    // accessoryCanvasObjects, skipping DRAIN_PAN/LIGHT_LINEAR/LIGHT). Real
    // implementation in Canvas3DGeometry (accessories land 6c2).
    this.redashAccessories(view);
    this.rotateTextIfNessecery(view);
  }

  /**
   * Overridable accessory re-dash sweep hook (setView :775 passes
   * is_position_changed=false; updatePosition :4847 passes true into
   * makeLineDashed's 6th arg). No-op until the accessory geometry port
   * (6c2) — the registry is empty so the legacy sweep would be a no-op too.
   */
  redashAccessories(_view: string, _isPositionChanged = false): void {
    // Canvas3DGeometry overrides once accessoryCanvasObjects fills (6c2).
  }

  /**
   * zoom (canvas.view3d:812-825) — `canvas:zoom` toolbar steps. [NEG]#5: the
   * legacy `this.canvas3d` indirection (only safe through the tunnel-view
   * binding) is ported as a plain method; step/clamp semantics identical:
   * +0.05 blocked above 2, −0.05 blocked below 0.3, then changeZoom +
   * updateProjectionMatrix (parity 64).
   */
  zoom(zoomType: 'zoom-in' | 'zoom-out'): void {
    this.need_update = true;
    const value = this.camera.zoom || 1;
    if (zoomType === 'zoom-in') {
      if (value > 2) return;
      this.camera.zoom = value > 3 ? 3 : value + 0.05;
    } else {
      if (value < 0.3) return;
      this.camera.zoom = value < 0.3 ? 0.3 : value - 0.05;
    }
    this.changeZoom();
    this.camera.updateProjectionMatrix();
  }

  /**
   * canvasRotation (canvas.view3d:1176-1180) — `canvas:rotate`. The legacy
   * comparison Math.round(y) != 23.79 is kept verbatim (it is effectively
   * always true, so enabling 360 always snaps to the default view first —
   * parity 44 documents the observable behavior).
   */
  canvasRotation(enabled: boolean): void {
    this.need_update = true;
    if (enabled && Math.round(this.camera.position.y) !== views.default[1]) this.setView('default');
    this.createViewControls(enabled);
  }

  /**
   * updatePosition (canvas.view3d:4833-4856) — OrbitControls hook, fired
   * while orbiting. First mirrors the vendored controls' DOM `.active` strip
   * into React state (activePreset = null), then the legacy grid-swap rule:
   * fixed grid when camera.y < 30 or zoomed out (<1) unless elevation is the
   * active preset (always cleared by the time this runs — same net result as
   * legacy, where the DOM class was already removed). [NEG]#11: re-dash skip
   * channel is the instance field.
   */
  updatePosition(): void {
    if (this.activePreset !== null) {
      this.activePreset = null;
      this.hooks.onViewChange(null);
    }
    if (
      this.camera.position.y < 30 ||
      !(this.camera.zoom >= 1 || this.activePreset === 'elevation_near_side')
    ) {
      this.fixedGridHelper.position.x = this.gridHelper.position.x;
      this.fixedGridHelper.position.z = this.gridHelper.position.z;
      this.gridHelper.visible = false;
      this.fixedGridHelper.visible = true;
    } else {
      this.gridHelper.visible = true;
      this.fixedGridHelper.visible = false;
    }
    const dmounts: any[] = [];
    Object.values(this.tunnelsDimountsDimensions).map((el: any) => el && dmounts.push(...el));
    this.showDimensions(dmounts);
    // Accessory wireframe re-dash to 'default' rules (canvas.view3d:4847,
    // gated by the is_position_changed channel — [NEG]#11 instance field).
    if (!this.is_position_changed) this.redashAccessories('default', true);
    if (this.text_view_position === 'plan' || this.text_view_position === 'elevation_near_side') {
      this.last_view = this.text_view_position;
      this.rotateTextIfNessecery('default');
    }
    this.showOrHideDimenssions([this.tunnelsHeightDimensions[1]], !this.activeTunnelIndex);
  }

  /**
   * showOrHideDimenssions (canvas.view3d:4875-4884) over dimension-group
   * entries ({width|length|height}_elements / elements + group). Guard order
   * fixed vs legacy (el null-check first — legacy dereferenced el.tunnel_id
   * before `!el`, crash-prone only on a path that never co-occurs live).
   */
  showOrHideDimenssions(list: any[], show: boolean, id?: number): void {
    list.map((el: any) => {
      if (!el || (id && el.tunnel_id === id)) return;
      const elements = el.width_elements || el.length_elements || el.height_elements || el.elements;
      if (show) {
        Object.values(elements).forEach((item: any) => (item.visible = false));
        elements.group.visible = show;
      } else Object.values(elements).forEach((item: any) => (item.visible = show));
    });
  }

  /** showDimensions (canvas.view3d:4885-4891). */
  showDimensions(dmounts: any[]): void {
    this.showOrHideDimenssions(
      [
        ...Object.values(this.tunnelsLengthDimensions),
        ...dmounts,
        ...Object.values(this.tunnelsSectionsLengthDimensions),
      ],
      true
    );
  }

  /**
   * changeZoom (canvas.view3d:4892-4896) — wheel-zoom hook + toolbar zoom +
   * setView. Grid choice by zoom >= 1 and the elevation preset; the legacy
   * `document.querySelector('li.active[data-value="elevation_near_side"]')`
   * is re-homed to activePreset (§3.5).
   */
  changeZoom(): void {
    const elevationActive = this.activePreset === 'elevation_near_side';
    this.gridHelper.visible = !!(this.camera.zoom >= 1 && !elevationActive);
    this.fixedGridHelper.visible = !!(this.camera.zoom < 1 || elevationActive);
    this.need_update = true;
  }

  /**
   * checkTunnelsLength (canvas.view3d:4897-4904) — pills lifecycle on (re)render:
   * init resets the active pill to 'all', otherwise prunes a stale id.
   */
  checkTunnelsLength(init = false): void {
    const list = this.getTunnels();
    if (init || list.length !== this.tunnels_length) {
      // The remembered pre-isolation pill can name a tunnel that no longer
      // exists (or predate a boot reset), so drop it with the selection.
      this.tunnelIndexBeforeViewIsolation = undefined;
      this.activeTunnelIndex = init
        ? null
        : list.find((el) => el.id === this.activeTunnelIndex)?.id ?? null;
      this.renderTunnelsIndexes();
      this.showHideLugsOnAdjoiningBase();
    }
    this.tunnels_length = list.length;
  }

  /**
   * rotateTextIfNessecery (canvas.view3d:4905-4935) — label mirroring for
   * back_left/back_right (unit −1). Operates on sectionCanvasObjects (empty
   * until 6c) — bookkeeping (text_view_position) must run now so the first
   * 6c draw lands in the correct orientation state.
   */
  rotateTextIfNessecery(view: string): void {
    if (
      (view === 'back_left' && this.text_view_position !== 'back_left') ||
      (view === 'back_right' && this.text_view_position !== 'back_right')
    ) {
      this.text_view_position = view;
      this.sectionCanvasObjects.forEach((el: any) => {
        const textBoundingBox = el.textLabel.geometry.boundingBox;
        const textBoundingBoxRotated = el.textLabelRotated.geometry.boundingBox;
        const textWidthRotated = textBoundingBoxRotated.max.x - textBoundingBoxRotated.min.x;
        const textWidth = textBoundingBox.max.x - textBoundingBox.min.x;
        el.textLabel.rotation.y = -Math.PI / 2;
        el.textLabel.position.x = -(textBoundingBox.max.z - textBoundingBox.min.z) / 2;
        el.textLabel.position.z = -textWidth / 2;
        el.textLabelRotated.position.x =
          -(textBoundingBoxRotated.max.z - textBoundingBoxRotated.min.z) / 2;
        el.textLabelRotated.rotation.y = -Math.PI / 2;
        this.getTextPosition(view, el.textLabelRotated, el.cube, textWidthRotated);
      });
    } else if (view !== 'back_left' && view !== 'back_right') {
      this.text_view_position = view;
      this.sectionCanvasObjects.forEach((el: any) => {
        const textBoundingBox = el.textLabel.geometry.boundingBox;
        const textBoundingBoxRotated = el.textLabelRotated.geometry.boundingBox;
        const textWidthRotated = textBoundingBoxRotated.max.x - textBoundingBoxRotated.min.x;
        const textWidth = textBoundingBox.max.x - textBoundingBox.min.x;
        el.textLabel.rotation.y = Math.PI / 2;
        el.textLabel.position.x = (textBoundingBox.max.z - textBoundingBox.min.z) / 2;
        el.textLabel.position.z = textWidth / 2;
        el.textLabelRotated.position.x =
          (textBoundingBoxRotated.max.z - textBoundingBoxRotated.min.z) / 2;
        el.textLabelRotated.rotation.y = Math.PI / 2;
        this.getTextPosition(view, el.textLabelRotated, el.cube, textWidthRotated);
      });
    }
  }

  /**
   * getAhuSizes (tunnel.js:375-444) — THE world-scale contract (parity 50/51):
   * aspect 12.5; 550/200 px reserves + dimensions_width (50 side-by-side,
   * 30·n stacked, + last tunnel width for next_to×3); coef = min of the three
   * ratio candidates clamped [0.1, 0.25] (preview 0.35); scene.scale =
   * 1 − ((diff>0 ? 5.5 : 4)·diff) capped 1.15 (main scene only, NOT the grid
   * scene); ~20 stamped fields. next_to = any tunnel with
   * current_index_values[1] < [0].
   *
   * Two deviations from that contract, both below at `dimensions_width` and both
   * explained there: a HYBRID unit counts 30 px per grid row/level rather than
   * per tunnel, and on every unit the whole reserve is capped at four fifths of
   * the element. The cap is reachable on a standard AHU too — only on a board
   * pane narrower than roughly 725 px (1 tunnel) to 988 px (8) or shorter than
   * ~550 px — where it draws the unit slightly larger than legacy does.
   */
  getAhuSizes(): { max_width: number; max_length: number } {
    let max_length = 0;
    let max_width = 0;
    let max_height = 0;
    let max_base_height = 0;
    let ahu_height = 0;
    let ahu_width = 0;
    let next_to = false;
    let coef = 0.2;
    const tunnels = this.getTunnels();
    let text_position: 'front' | 'top' = tunnels.length > 1 ? 'front' : 'top';
    let longestTunnelDirection: number | null = 1;

    tunnels.forEach((el, idx) => {
      ahu_width += el.width;
      if ((el.baseHeight || 0) > max_base_height) max_base_height = el.baseHeight || 0;
      if (el.width > max_width) max_width = el.width;
      if (el.length > max_length) {
        max_length = el.length;
        this.max_tunnel_airflow = el.airflow_direction_id;
        longestTunnelDirection = el.airflow_direction_id;
      }
      if (el.height + (el.baseHeight || 0) > max_height) max_height = el.height + (el.baseHeight || 0);
      ahu_height += el.height + (el.baseHeight || 0);
      if (el.current_index_values[1] < el.current_index_values[0]) next_to = true;
      if (!idx) {
        this.firstTunnel = el.id;
        this.firstTunnelBaseHeight = el.baseHeight || 0;
        this.firstTunnelWidth = el.width;
        this.firstTunnelLength = el.length;
        this.firstTunnelHeight = el.height;
      }
      if (idx === tunnels.length - 1) this.lastTunnel = el.id;
    });

    // Hybrid AHU (KA-x) — correct the footprint the non-hybrid loop above
    // just computed. That loop assumes a single side-by-side OR stacked
    // strip (summing every tunnel's own width into `ahu_width`, every
    // tunnel's own height into `ahu_height`) — right for the 2-3-tunnel
    // next_to/stacked cases it was built for, but a genuine (row, level,
    // position) hybrid grid triple-counts: two back-to-back tunnels
    // (same row+level, chained in X) both add their width to `ahu_width`
    // even though they don't sit side-by-side, and likewise for two OU
    // levels' heights landing in `ahu_width`'s sibling sum. Left
    // uncorrected this can shrink a real hybrid AHU to a fraction of its
    // actual on-screen size. `max_length` and the `coef` clamps below are left
    // exactly as-is — this block only replaces the three raw footprint numbers
    // those formulas consume. (`dimensions_width` IS changed for a hybrid unit,
    // separately, where it is computed.)
    const hybridTunnels = tunnels.filter((t) => t.is_hybrid);
    /** Hybrid grid depth: how many SBS rows, and how many OU levels. */
    let hybridRows = 0;
    let hybridLevels = 0;
    if (hybridTunnels.length) {
      let minLeft = Infinity;
      let maxRight = -Infinity;
      hybridTunnels.forEach((t) => {
        const isRtl = t.airflow_direction_id === 2;
        const xOffset = t.x_offset || 0;
        const left = isRtl ? xOffset - t.length : xOffset;
        const right = left + t.length;
        if (left < minLeft) minLeft = left;
        if (right > maxRight) maxRight = right;
      });
      if (Number.isFinite(minLeft) && Number.isFinite(maxRight)) {
        max_length = Math.max(max_length, maxRight - minLeft);
      }
      // Z extent (row/width axis) — rows sit side-by-side; levels stack in
      // Y and back-to-back positions chain in X, so only the widest tunnel
      // PER ROW should contribute, not every tunnel.
      const widthByRow = new Map<number, number>();
      hybridTunnels.forEach((t) => {
        const row = t.row ?? 1;
        const w = widthByRow.get(row) ?? 0;
        if ((t.width || 0) > w) widthByRow.set(row, t.width || 0);
      });
      ahu_width = [...widthByRow.values()].reduce((a, b) => a + b, 0);
      // Y extent (level axis) — the highest roof on the unit, which is what
      // the backend's layout and Model View measure it as. Adding one height
      // per level (the tallest tunnel of each) is only the same number when
      // every level stands on the tallest tunnel of the one below: on AHU
      // 3985 a 100" tunnel standing on a 62" one made a 166" unit 204" tall,
      // and the overall-height dimension said so.
      const heightByLevel = new Map<number, number>();
      hybridTunnels.forEach((t) => {
        const level = t.level ?? 1;
        const h = heightByLevel.get(level) ?? 0;
        const th = (t.height || 0) + (t.baseHeight || 0);
        if (th > h) heightByLevel.set(level, th);
      });
      ahu_height =
        stackedAhuHeight(hybridTunnels) ||
        [...heightByLevel.values()].reduce((a, b) => a + b, 0);
      // Section names (KA-x). `tunnels.length > 1` above is a stand-in for
      // "the tunnels are stacked, so the top face belongs to the tunnel
      // above" — true of a standard 2-tunnel OU AHU, not of a hybrid grid
      // one row deep and one level tall. That grid is a single horizontal
      // strip: its top face is as free as a single-tunnel AHU's, while the
      // front face is the one carrying the airflow arrows and the length
      // dimensions of every back-to-back position in the strip. So put the
      // names on top there, exactly as the single-tunnel case does.
      if (
        hybridTunnels.length === tunnels.length &&
        widthByRow.size === 1 &&
        heightByLevel.size === 1
      )
        text_position = 'top';
      hybridRows = widthByRow.size;
      hybridLevels = heightByLevel.size;
    }

    const aspect = 12.5;
    // `dimensions_width` reserves screen room for the dimension lines drawn
    // outside the cabinet. Legacy's `30 × tunnels` is "one outward band per
    // stacked tunnel", which is right while a tunnel count IS a stack depth —
    // true of every standard AHU, false of a hybrid one, where a (row, level)
    // cell holds a whole back-to-back chain and each of its tunnels is another
    // section, not another dimension column. (Bands are allocated by overlap,
    // `canvas3dGeometry.assignDimensionBands`, and only spans that are
    // IDENTICAL share one — so a 15-tunnel chain of equal-width tunnels draws a
    // single band, while one whose tunnels differ in width or height draws a few.
    // 30 px per grid row/level is therefore a margin heuristic, not the band
    // count: each extra band steps ~12.5 px, which fits inside it.)
    //
    // Unreserved, a hybrid AHU shrank by 30px of reserve for every section
    // added — the "canvas progressively zooms out as you add sections" report —
    // and past ~28 tunnels the reserve exceeded the canvas, `canvas_height`
    // went negative, and `coef` with it: `scene.scale` below then comes out
    // NEGATIVE, which is a point reflection, and the whole unit draws mirrored.
    // That is the "AHU flips upside down after a refresh" report; it reproduces
    // on a 36-tunnel unit at 1920x1080 (scale −0.54) and needs fewer tunnels on
    // a smaller screen. Both reported during hybrid beta testing, 2026-10-15.
    const dimension_bands = hybridLevels ? Math.max(hybridRows, hybridLevels) : tunnels.length;
    let dimensions_width = next_to ? 50 : 30 * dimension_bands;
    if (next_to && tunnels.length === 3) dimensions_width += tunnels[tunnels.length - 1].width;
    const isAccessoryPreview = this.previewAccessoryId != null;
    // …and the reserve is a margin, never a hard claim on the canvas: cap it at
    // four fifths of the element so what is left to draw in always stays
    // positive.
    //
    // This cap applies to every unit, standard ones included. At the board's
    // usual pane size nothing reaches it — a standard AHU reserves at most
    // 550+240 px and `#canvas_section` is ~1338 px wide at a 1600 px viewport —
    // but it DOES engage on a narrow or short pane: below roughly 725 px wide
    // for a 1-tunnel unit, 838 px for 4, 988 px for 8, or ~400–550 px tall.
    // There it draws the unit slightly larger than legacy would, and it stops a
    // small window from driving `scene.scale` negative the way a big hybrid did.
    const reserve = (fixed: number, available: number): number =>
      Math.min(fixed + dimensions_width, available * 0.8);
    const canvas_width = isAccessoryPreview
      ? (this.el.offsetWidth - 20) / aspect
      : (this.el.offsetWidth - reserve(550, this.el.offsetWidth)) / aspect;
    const canvas_height = isAccessoryPreview
      ? (this.el.offsetHeight - 20) / aspect
      : (this.el.offsetHeight - reserve(200, this.el.offsetHeight)) / aspect;
    coef = canvas_height / (1.5 * ahu_width);
    let val = canvas_width / (max_length + ahu_width);
    if (val < coef) coef = val;
    this.max_height = max_height;
    val = canvas_height / (2 * (next_to ? max_height : ahu_height));
    if (val < coef) coef = val;
    const maxCoef = isAccessoryPreview ? 0.35 : 0.25;
    this.coef = coef < 0.1 ? 0.1 : coef > maxCoef ? maxCoef : coef;
    const diff = this.coef - coef;
    const scale = 1 - (diff > 0 ? 5.5 : 4) * diff;
    this.scene.scale.x = scale > 1.15 ? 1.15 : scale;
    this.scene.scale.y = scale > 1.15 ? 1.15 : scale;
    this.scene.scale.z = scale > 1.15 ? 1.15 : scale;
    this.ahu_height = next_to ? 0 : ahu_height * this.coef;
    this.ahu_width = next_to ? ahu_width * this.coef : 0;
    this.ahu_length = next_to ? max_length * this.coef : 0;
    this.tunels_count = tunnels.length;
    this.text_position = next_to ? 'top' : text_position;
    this.max_length = max_length;
    this.max_height = max_height;
    this.max_width = max_width;
    this.next_to = next_to;
    this.max_base_height = max_base_height * this.coef;
    this.longestTunnelDirection = longestTunnelDirection;
    return { max_width, max_length };
  }

  /** updateCameraProps (tunnel.js:611-621) — per-camera resize reset. */
  updateCameraProps(camera: THREE.OrthographicCamera, gridHelper: THREE.GridHelper): void {
    camera.zoom = 1;
    gridHelper.scale.set(1.5, 1.5, 1.5);
    camera.left = this.el.offsetWidth / -25;
    camera.right = this.el.offsetWidth / 25;
    camera.top = this.el.offsetHeight / 25;
    camera.bottom = this.el.offsetHeight / -25;
    camera.updateProjectionMatrix();
  }

  /**
   * redrawCanvas (tunnel.js:622-646) — THE resize contract (parity 48).
   * `fromAhu` = the `redraw:canvas` bus path (camera/zoom untouched); resize
   * passes false and resets both cameras' frusta + zoom. Renderer re-sized and
   * DPR re-clamped on both paths; geometry registries wiped and re-rendered
   * (the tunnel re-render itself is the 6c attach point). The legacy
   * simulateZoomApp() call on resize events lives in useBodyZoom (parity 9).
   */
  redrawCanvas(fromAhu: boolean): void {
    this.need_update = true;
    if (!fromAhu) {
      this.updateCameraProps(this.camera, this.gridHelper);
      this.updateCameraProps(this.cameraForGrid, this.fixedGridHelper);
    }
    this.getAhuSizes();
    this.renderer.setSize(this.el.offsetWidth, this.el.offsetHeight);
    this.renderer.setPixelRatio(Math.min(Math.max(1.5, window.devicePixelRatio), 2));
    this.sectionCanvasObjects = [];
    this.accessoryCanvasObjectsObj = {};
    this.tunnelsDimensions = {};
    this.tunnels_lugs = {};
    this.tunnels_lugs_on_canvas = {};
    this.activeLugs = [];
    // 6c: render_canvas_tunnel(el, true, idx+1, max_length, max_width) per
    // tunnel + 300 ms dmount re-schedule (tunnel.js:634-645).
  }

  /** change_canvas_color_mode receiver (canvas.view3d:116-119, preview forces false). */
  setShowColor(val: boolean): void {
    this.showColor = this.previewAccessoryId != null ? false : val;
  }

  // --- 6c attach points (registries empty in 6b; calls must be no-ops) ---

  /** 6c: lug visibility on adjoining bases (canvas.view3d showHideLugsOnAdjoiningBase). */
  showHideLugsOnAdjoiningBase(): void {
    // no-op until the lug geometry port (6c).
  }

  /** 6c: per-view label positioning router (canvas.view3d getTextPosition). */
  getTextPosition(_view: string, _label: any, _cube: any, _textWidth: number): void {
    // no-op until the label geometry port (6c).
  }

  /**
   * Text-label geometry cache — see `Canvas3DGeometry.createTextLabel`.
   *
   * Keyed on everything that changes the SHAPE (the string, size, extrusion
   * height and the two bake-time rotations); the colour lives on the material,
   * which is never shared. Per INSTANCE, not module-global, so a disposed
   * canvas cannot hand disposed GPU buffers to the next one — a context-loss
   * rebuild constructs a new Canvas3D and starts with an empty cache.
   */
  protected textGeometryCache = new Map<string, THREE.BufferGeometry>();

  /**
   * Hard bound on that cache.
   *
   * `AhuCanvas` keys its <canvas> on a context-loss `resetKey`, NOT on the
   * AHU, so one `Canvas3D` outlives every unit the user opens in a session —
   * and the cache holds a strong reference to each geometry, so its GPU
   * buffer stays allocated after nothing draws that string any more. A board
   * uses only tens of distinct strings, but every section resize and every
   * new unit mints more, so the set is unbounded over a session. Without this
   * the PR would trade a per-draw cost for a per-session leak.
   *
   * At the cap the whole cache is dropped rather than evicted entry by entry:
   * an LRU would put bookkeeping on the hot path for a case a normal session
   * never reaches. Dropping it is safe even for strings currently on screen —
   * `BufferGeometry.dispose()` only releases the GPU buffer, keeping the
   * CPU-side attributes, and three.js re-uploads them on the next render. The
   * cost is one re-upload, never a missing label.
   */
  protected static readonly TEXT_GEOMETRY_CACHE_MAX = 512;

  /**
   * Dispose a geometry unless the text-label cache owns it.
   *
   * Every per-section / per-tunnel teardown below walks meshes and disposes
   * their geometry, and a cached label geometry is referenced by every mesh
   * showing that string. Freeing it with the first of them would not lose
   * the label — `BufferGeometry.dispose()` in three r110 drops only the GPU
   * buffers and the next render rebuilds them from the CPU-side attributes —
   * but it would cost a re-upload for every surviving mesh, on every
   * teardown, which is most of what the cache exists to avoid. So the cache
   * is the single owner: it frees its entries in `dispose()`, and in
   * `createTextLabel` when it reaches `TEXT_GEOMETRY_CACHE_MAX`.
   */
  disposeGeometry(geometry: any): void {
    if (!geometry) return;
    if (geometry.userData?.sharedTextLabel) return;
    geometry.dispose?.();
  }

  /**
   * Dispose every cached label geometry and empty the cache.
   *
   * Called from `dispose()` and from `createTextLabel` at the cap. In the cap
   * case, meshes on screen may still reference these geometries: that is safe
   * for the reason in `disposeGeometry` above — they re-upload on the next
   * render — and they lose the `sharedTextLabel` flag here, so a later
   * teardown frees them normally instead of leaking.
   */
  protected disposeTextGeometryCache(): void {
    this.textGeometryCache.forEach((geometry) => {
      // Clear the flag first, or `disposeGeometry` would skip the very
      // geometries this is meant to free.
      if (geometry.userData) delete geometry.userData.sharedTextLabel;
      geometry.dispose();
    });
    this.textGeometryCache.clear();
  }

  /**
   * G39 disposeChilds (canvas.view3d:3582-3612): recursive geometry +
   * material + 6 texture-map slots disposal. Needed from 6b on for the React
   * unmount contract (parity 49 — legacy never unmounts).
   *
   * The guard is on `element` alone, NOT on `element.children.length`. A
   * CHILDLESS object — a tunnel wireframe, a section wireframe, a text label,
   * an accessory shape — is what most of the teardown paths hand this
   * function, and bailing out on one skipped the lines that free the object's
   * OWN geometry and material. Such a geometry stays registered in the
   * renderer's `WebGLGeometries`, which holds the strong reference, so neither
   * its GPU buffer nor its CPU attributes can go, and
   * `renderer.info.memory.geometries` grows by one per leaf per redraw.
   * Measured before the guard moved: +1 per tunnel on every full redraw of a
   * standard AHU and of a hybrid, for ever, and ~350 per AHU switch.
   *
   * So the set this frees is deliberately WIDER than before: a childless
   * object's own geometry and material, and the texture slots of whatever
   * object is handed in, now go too. What does not change is the per-object
   * work — each object in the subtree is visited once, as itself, instead of a
   * parent also reaching into its children — or the label cache's hold on
   * shared geometry, which `disposeGeometry` still honours.
   *
   * Some callers free the same object again right after calling this, which is
   * now redundant: `disposeDetails`, and `normalizeTunnelBase`'s inline
   * `baseCube.geometry.dispose()`. Harmless — three.js `dispose()` only
   * dispatches an event, and the renderer has already dropped its listener —
   * so untangling them is a separate cleanup, not part of this fix.
   */
  disposeChilds(element: any): void {
    if (!element) return;
    (element.children || []).forEach((child: any) => this.disposeChilds(child));
    this.disposeGeometry(element.geometry);
    if (element.material) {
      const materials = Array.isArray(element.material) ? element.material : [element.material];
      materials.forEach((material: any) => {
        if (material.map) material.map.dispose();
        if (material.lightMap) material.lightMap.dispose();
        if (material.bumpMap) material.bumpMap.dispose();
        if (material.normalMap) material.normalMap.dispose();
        if (material.specularMap) material.specularMap.dispose();
        if (material.envMap) material.envMap.dispose();
        material.dispose();
      });
    }
  }

  /**
   * Unmount / reset disposal (parity 49 + beforeunload contract parity 47):
   * stops the rAF loop, disposes both controls, walks both scenes, disposes
   * the renderer and (optionally) forces context loss so route changes do not
   * accumulate WebGL contexts. Safe to call twice.
   */
  dispose({ forceContextLoss = true }: { forceContextLoss?: boolean } = {}): void {
    if (this.disposed) return;
    this.disposed = true;
    // Release the live-canvas registry handle (Item 1 — was the implicit
    // dangling window.__c3d). No-op if a newer instance already took over.
    clearLiveCanvas3D(this);
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this._controls?.dispose();
    this._controlsForGrid?.dispose();
    this._controls = null;
    this._controlsForGrid = null;
    this.disposeChilds(this.scene);
    this.disposeChilds(this.sceneForGrid);
    // After the scene walks, which deliberately leave the shared label
    // geometries alone: this is the one owner that frees them.
    this.disposeTextGeometryCache();
    try {
      this.renderer.dispose();
      if (forceContextLoss) this.renderer.forceContextLoss();
    } catch {
      // context may already be lost — disposal stays best-effort.
    }
  }
}
